<template>
  <div class="space-y-10 divide-y divide-gray-900/10">
    <div class="grid grid-cols-1 gap-x-8 gap-y-8 pt-10 md:grid-cols-3">
      <div class="px-4 sm:px-0">
        <h2 class="text-base font-semibold leading-7 text-gray-900">Create Question</h2>
        <p class="mt-1 text-sm leading-6 text-gray-600">Question Text Prompt</p>
      </div>

      <Form v-slot="{ handleSubmit, errors}" :validation-schema="schema" @invalid-submit="onInvalidSubmit" class="bg-white shadow-sm ring-1 ring-gray-900/5 sm:rounded-xl md:col-span-2">
        <button v-if="questionId" @click="destroy" type="button" class="rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600 m-3">
          <TrashIcon class="h-5 w-5" aria-hidden="true" />
        </button>
        <div class="px-4 py-6 sm:p-8">
          <div class="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
            <div class="sm:col-span-5">
              <label for="section" class="block text-sm font-medium leading-6 text-gray-900">
                Business Function
                <span class="text-red-600 text-sm">*</span>
              </label>
              <div class="mt-2">
                <Field v-model="formData.question_section_id" v-bind:class="{ 'ring-red-600': errors.section }" as="select" name="section" id="section" class="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6">
                  <option value="" disabled>Select Business Function</option>
                  <option v-for="item in questionSectionList" :value="item.id"
                          v-bind:selected="item.id === formData.question_section_id">
                    {{item.parent.title}} > {{ item.title }}
                  </option>
                </Field>
                <ErrorMessage class="mt-2 text-xs text-red-600" name="section" />
              </div>
            </div>
            <div class="sm:col-span-5">
              <label for="parent" class="block text-sm font-medium leading-6 text-gray-900">Parent Question</label>
              <div class="mt-2">
                <Field v-model="formData.parent_id" v-bind:class="{ 'ring-red-600': errors.parent }" as="select" name="parent" id="parent" class="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6">
                  <option value="0">Select Parent Question</option>
                  <option v-for="item in parentList" :value="item.id"
                          v-bind:selected="item.id === formData.parent_id">
                    {{ item.text }}
                  </option>
                </Field>
                <ErrorMessage class="mt-2 text-xs text-red-600" name="parent" />
              </div>
            </div>
            <div class="sm:col-span-5">
              <label for="option_group" class="block text-sm font-medium leading-6 text-gray-900">
                Option Group
                <span class="text-red-600 text-sm">*</span>
              </label>
              <div class="mt-2">
                <Field v-model="formData.options_group_id" v-bind:class="{ 'ring-red-600': errors.option_group }" as="select" name="option_group" id="option_group" class="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6">
                  <option value="" disabled>Select Option group</option>
                  <option v-for="item in optionGroupList" :value="item.id"
                          v-bind:selected="item.id === formData.option_group">
                    {{ item.name }}
                  </option>
                </Field>
                <ErrorMessage class="mt-2 text-xs text-red-600" name="option_group" />
              </div>
            </div>
            <div class="sm:col-span-5">
              <label for="text" class="block text-sm font-medium leading-6 text-gray-900">
                Text
                <span class="text-red-600 text-sm">*</span>
              </label>
              <div class="mt-2">
                <Field v-model="formData.text" v-bind:class="{ 'ring-red-600': errors.text }" as="textarea" name="text" id="text" placeholder="Text" rows="3" class="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6" />
                <ErrorMessage class="mt-2 text-xs text-red-600" name="text" />
              </div>
            </div>
            <div class="sm:col-span-5">
              <label for="description" class="block text-sm font-medium leading-6 text-gray-900">Description</label>
              <div class="mt-2">
                <TrixComponent v-if="editorView" :id="'description'" :text="formData.description" @dataFromTrix="getDataDescriptionFromTrix"></TrixComponent>
                <Field v-model="formData.description" as="textarea" name="description" id="description" hidden="hidden"/>
                <ErrorMessage class="mt-2 text-xs text-red-600" name="description" />
              </div>
            </div>

            <div class="sm:col-span-5">
              <label for="additional_info" class="block text-sm font-medium leading-6 text-gray-900">Additional Info</label>
              <div class="mt-2">
                <Field v-model="formData.additional_info" v-bind:class="{ 'ring-red-600': errors.additional_info }" type="text" name="additional_info" id="additional_info" placeholder="Additional info" class="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6" />
                <ErrorMessage class="mt-2 text-xs text-red-600" name="additional_info" />
              </div>
            </div>
            <div class="sm:col-span-5">
              <label for="info_link " class="block text-sm font-medium leading-6 text-gray-900">Info Link</label>
              <div class="mt-2">
                <Field v-model="formData.info_link" v-bind:class="{ 'ring-red-600': errors.info_link }" type="text" name="info_link" id="info_link" placeholder="Info link" class="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6" />
                <ErrorMessage class="mt-2 text-xs text-red-600" name="info_link" />
              </div>
            </div>
            <div class="sm:col-span-5">
              <label for="additional_info_1" class="block text-sm font-medium leading-6 text-gray-900">Additional Info</label>
              <div class="mt-2">
                <Field v-model="formData.additional_info_1" v-bind:class="{ 'ring-red-600': errors.additional_info_1 }" type="text" name="additional_info_1" id="additional_info_1" placeholder="Additional info" class="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6" />
                <ErrorMessage class="mt-2 text-xs text-red-600" name="additional_info_1" />
              </div>
            </div>
            <div class="sm:col-span-5">
              <label for="info_link_1" class="block text-sm font-medium leading-6 text-gray-900">Info Link</label>
              <div class="mt-2">
                <Field v-model="formData.info_link_1" v-bind:class="{ 'ring-red-600': errors.info_link_1 }" type="text" name="info_link_1" id="info_link_1" placeholder="Info link" class="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6" />
                <ErrorMessage class="mt-2 text-xs text-red-600" name="info_link_1" />
              </div>
            </div>

            <div class="sm:col-span-5">
            <div class="relative flex gap-x-3">
              <div class="flex h-6 items-center">
                <input v-model="formData.no_score_completion" id="no_score_completion" name="no_score_completion" type="checkbox" class="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600" />
              </div>
              <div class="text-sm leading-6">
                <label for="no_score_completion" class="font-medium text-gray-900">Allow completion without scoring </label>
                <p class="text-gray-500">Text Prompt</p>
              </div>
            </div>
            </div>
            <div class="sm:col-span-5">
              <label for="severity" class="block text-sm font-medium leading-6 text-gray-900">Severity</label>
              <div class="mt-2">
                <Field v-model="formData.severity" v-bind:class="{ 'ring-red-600': errors.severity }" as="select" name="severity" id="severity" class="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6">
                  <option value="">Select Option group</option>
                  <option v-for="item in severityList" :value="item.id"
                          v-bind:selected="item.id === formData.severity">
                    {{ item.name }}
                  </option>
                </Field>
                <ErrorMessage class="mt-2 text-xs text-red-600" name="severity" />
              </div>
            </div>
            <div class="sm:col-span-5">
              <label for="firm_types" class="block text-sm font-medium leading-6 text-gray-900">
                Question Tags
              </label>
              <div class="mt-2">
                <Field name="firm_types" v-model="formData.question_tags" v-slot="{ componentField }">
                  <v-select
                      v-bind="componentField"
                      multiple
                      :options="questionTagsList"
                      label="name" />
                </Field>
                <ErrorMessage class="mt-2 text-xs text-red-600" name="firm_types" />
              </div>
            </div>
          </div>
        </div>

        <div class="flex items-center justify-end gap-x-6 border-t border-gray-900/10 px-4 py-4 sm:px-8">
          <button @click="cancel" type="button" class="text-sm font-semibold leading-6 text-gray-900">Cancel</button>
          <button v-if="questionId" @click="handleSubmit($event, update)" type="button" class="rounded-md bg-gray-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-600">Update</button>
          <button v-else @click="handleSubmit($event, create)" type="button" class="rounded-md bg-gray-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-600">Save</button>
        </div>

      </Form>
    </div>
  </div>
</template>

<script>
import { PhotoIcon, UserCircleIcon, TrashIcon } from '@heroicons/vue/24/solid'
import TrixComponent from "../TrixComponent.vue";

export default {
  name: "QuestionCreateEditFormComponent",

  props: {
    questionSetId: {
      type: Number,
      required: true,
    },
    questionSectionId: {
      type: Number,
      required: true,
    },
    questionId: {
      type: Number,
      required: false,
    },
  },

  data() {
    return {
      questionSetList: [],
      questionSectionList: [],
      parentList: [],
      optionGroupList: [],
      severityList: [],
      questionTagsList: [],
      formData: {
        question_set_id: this.questionSetId,
        question_section_id: null,
        parent_id: 0,
        options_group_id: null,
        text: null,
        description: null,
        additional_info: null,
        info_link: null,
        additional_info_1: null,
        info_link_1: null,
        no_score_completion: false,
        severity: null,
        question_tags: [],
      },
      editorView: false,
    }
  },
  components: {
    PhotoIcon,
    UserCircleIcon,
    TrashIcon,
    TrixComponent,
  },
  computed: {
    schema() {
      return yup.object({
        // question_set: yup.number().required().label('Question Set'),
        section: yup.number().required().label('Question Section'),
        parent: yup.number().nullable().label('Parent Question'),
        option_group: yup.number().required().label('Option Group'),
        text: yup.string().required().min(3).label('Text'),
        description: yup.string().nullable().label('Description'),
        additional_info: yup.string().nullable().label('Additional info'),
        info_link: yup.string().nullable().label('Info link'),
        additional_info_1: yup.string().nullable().label('Additional info'),
        info_link_1: yup.string().nullable().label('Info link'),
        severity: yup.number().required().label('Severity'),
        question_tags: yup.array().min(1).label('Question Tags'),
      });
    },
  },
  methods: {
    getQuestionSectionList() {
      axios.get(`/member-firm/question-set/question/form-data/question-section-list/${this.questionSectionId}`).then((res) => {
        this.questionSectionList = res.data.data;
        if (!this.questionId) {
          this.formData.question_section_id = this.questionSectionId;
        }
      }).catch((error) => {
        console.log('error', error)
      })
    },
    getParentQuestionList() {
      axios.get(`/member-firm/question-set/question/form-data/question-parent-list/${this.questionSetId}/question-section/${this.questionSectionId}`).then((res) => {
        this.parentList = res.data.data;
      }).catch((error) => {
        console.log('error', error)
      })
    },
    getOptionGroupList() {
      axios.get(`/member-firm/question-set/question/form-data/question-option-group-list/${this.questionSectionId}`).then((res) => {
        this.optionGroupList = res.data.data;
      }).catch((error) => {
        console.log('error', error)
      })
    },
    getSeverityList() {
      axios.get(`/member-firm/question-set/question/form-data/question-severity-list`).then((res) => {
        this.severityList = res.data.data;
        this.editorView = true;
      }).catch((error) => {
        console.log('error', error)
      })
    },
    getQuestionTagsList() {
      axios.get(`/member-firm/question-set/question/form-data/question-tag-list`).then((res) => {
        this.questionTagsList = res.data.data;
      }).catch((error) => {
        console.log('error', error)
      })
    },
    getQuestion() {
      axios.get(`/member-firm/question-set/${this.questionSetId}/question/edit/${this.questionId}`).then((res) => {
        this.formData = res.data.data;
      }).catch((error) => {
        console.log('error', error)
      })
    },
    update() {
      axios.put(`/member-firm/question-set/${this.questionSetId}/question/update/${this.questionId}`, this.formData)
          .then((res) => {
            this.$emit('reloadIndex');
            this.$notify({ type: "success", title: "Successfully", text: res.data.message });
          })
          .catch((error) => {
            console.log('error', error);
            this.$emit('reloadIndex');
            this.$notify({ title: "Unsuccessfully", text: error.message });
          });
    },
    create() {
      axios.post(`/member-firm/question-set/question/store`, this.formData)
          .then((res) => {
            this.$emit('reloadIndex');
            this.$notify({ type: "success", title: "Successfully", text: res.data.message });
          })
          .catch((error) => {
            console.log('error', error);
            this.$emit('reloadIndex');
            this.$notify({ title: "Unsuccessfully", text: error.message });
          });
    },
    cancel() {
      this.$emit('cancel');
    },
    destroy() {
      if(confirm("Do you really want to delete?")) {
        axios.delete(`/member-firm/question-set/question/destroy/${this.questionId}`).then((res) => {
          this.$notify({ type: "success", title: "Successfully", text: res.data.message });
          this.$emit('cancel');
          this.$emit('reloadIndex')
        }).catch((error) => {
          console.log('error', error)
          this.$notify({ title: "Unsuccessfully", text: error.message });
          this.$emit('reloadIndex');
        })
      }
    },
    onInvalidSubmit({ values, errors, results }) {
      const firstErrorFieldName = Object.keys(errors)[0];
      const el = document.querySelector(`[name="${firstErrorFieldName}"]`);
      if (el) {
        el.scrollIntoView({block: "center"});
      }
    },
    getDataDescriptionFromTrix(data) {
      this.formData.description = data;
      this.editorView = true;
    },
  },
  created() {
    if (this.questionSetId && this.questionId) {
      this.getQuestion();
    }
    this.getQuestionSectionList();
    this.getParentQuestionList();
    this.getOptionGroupList();
    this.getSeverityList();
    this.getQuestionTagsList();
  }
}
</script>